import "~/css/toast.css";

// Sonner's behaviour in vanilla JS (https://sonner.emilkowal.ski): a stack of up to three visible toasts, expanded
// while hovered or focused (timers pause meanwhile, and while the tab is hidden), swipe up to dismiss, Alt+T to reach
// them from the keyboard, updates by id. API: toast(), toast.success/error/info/warning/loading/promise/dismiss.

const VISIBLE_TOASTS = 3;
const GAP = 14;
const SWIPE_THRESHOLD = 45;
const UNMOUNT_DELAY = 200;
const DURATION = { error: 8000, loading: Infinity };
const DEFAULT_DURATION = 5000;
const PENDING_KEY = "galada:toast";

const solid = (path) =>
	`<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="${path}"/></svg>`;
const ICONS = {
	success: solid(
		"M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z",
	),
	error: solid(
		"M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
	),
	warning: solid(
		"M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
	),
	info: solid(
		"M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z",
	),
	loading: `<div class="toast__spinner" aria-hidden="true">${Array.from({ length: 12 }, (_, bar) => `<span style="--bar: ${bar}"></span>`).join("")}</div>`,
};
const CLOSE_ICON =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

const toasts = []; // newest first
let toaster = null;
let expanded = false;
let nextId = 0;

const paused = () => expanded || document.hidden;
// Fanned out while the pointer is over the stack or keyboard focus is inside it (a click's focus doesn't count).
const engaged = () =>
	Boolean(toaster?.matches(":hover") || toaster?.querySelector(":focus-visible"));

function startTimer(item) {
	clearTimeout(item.timer);
	item.timer = null;
	if (!Number.isFinite(item.remaining) || paused()) return;
	item.startedAt = Date.now();
	item.timer = setTimeout(() => dismiss(item.id), item.remaining);
}

function pauseTimer(item) {
	if (!item.timer) return;
	clearTimeout(item.timer);
	item.timer = null;
	item.remaining -= Date.now() - item.startedAt;
}

const syncTimers = () => {
	for (const item of toasts) {
		if (paused()) pauseTimer(item);
		else if (!item.timer) startTimer(item);
	}
};

function setExpanded(value) {
	if (expanded === value) return;
	expanded = value;
	layout();
	syncTimers();
}

function layout() {
	if (!toaster) return;
	let offset = 0;
	toasts.forEach((item, index) => {
		const { style, dataset } = item.element;
		style.setProperty("--index", index);
		style.setProperty("--z-index", toasts.length - index);
		style.setProperty("--offset", `${offset}px`);
		style.setProperty("--initial-height", `${item.height}px`);
		dataset.front = String(index === 0);
		dataset.visible = String(index < VISIBLE_TOASTS);
		offset += item.height + GAP;
	});
	toaster.dataset.expanded = String(expanded && toasts.length > 1);
	toaster.style.setProperty("--front-toast-height", `${toasts[0]?.height ?? 0}px`);
}

// In the open modal (if any), so the stack is not inert under it; the popover puts it in the top layer.
function mountToaster() {
	if (!toaster) {
		toaster = document.createElement("ol");
		toaster.className = "toaster";
		toaster.popover = "manual";
		toaster.tabIndex = -1;
		toaster.setAttribute("aria-label", `${gettext("Notifiche")} (Alt+T)`);
		toaster.setAttribute("aria-live", "polite");
		toaster.addEventListener("mouseenter", () => setExpanded(true));
		toaster.addEventListener("mouseleave", () => setExpanded(engaged()));
		toaster.addEventListener("focusin", () => setExpanded(engaged()));
		toaster.addEventListener("focusout", () => requestAnimationFrame(() => setExpanded(engaged())));
	}
	const host =
		[...document.querySelectorAll("dialog:modal")].findLast(
			(dialog) => dialog.dataset.state !== "closing",
		) ?? document.body;
	if (toaster.parentElement !== host) {
		if (toaster.matches(":popover-open")) toaster.hidePopover();
		host.append(toaster);
		if (host !== document.body) {
			host.addEventListener(
				"close",
				() => {
					if (toaster.parentElement !== host) return;
					toaster.hidePopover();
					document.body.append(toaster);
					if (toasts.length) toaster.showPopover();
				},
				{ once: true },
			);
		}
	}
	if (!toaster.matches(":popover-open")) toaster.showPopover();
}

function render(item, type, message, { description, action, closeButton }) {
	const { element } = item;
	element.dataset.type = type;
	element.setAttribute("role", type === "error" ? "alert" : "status");
	const parts = [];
	if (ICONS[type]) {
		const icon = document.createElement("div");
		icon.className = "toast__icon";
		icon.innerHTML = ICONS[type];
		parts.push(icon);
	}
	const content = document.createElement("div");
	content.className = "toast__content";
	const title = document.createElement("div");
	title.className = "toast__title";
	title.textContent = message;
	content.append(title);
	if (description) {
		const text = document.createElement("div");
		text.className = "toast__description";
		text.textContent = description;
		content.append(text);
	}
	parts.push(content);
	if (action) {
		const button = document.createElement("button");
		button.type = "button";
		button.className = "toast__action";
		button.textContent = action.label;
		button.addEventListener("click", (event) => {
			action.onClick(event);
			if (!event.defaultPrevented) dismiss(item.id);
		});
		parts.push(button);
	}
	if (closeButton ?? (type === "error" || Boolean(action))) {
		const close = document.createElement("button");
		close.type = "button";
		close.className = "toast__close";
		close.setAttribute("aria-label", gettext("Chiudi"));
		close.innerHTML = CLOSE_ICON;
		close.addEventListener("click", () => dismiss(item.id));
		parts.push(close);
	}
	element.replaceChildren(...parts);
	const height = element.style.height;
	element.style.height = "auto";
	item.height = element.getBoundingClientRect().height;
	element.style.height = height;
}

function bindSwipe(item) {
	const { element } = item;
	let start = null;
	element.addEventListener("pointerdown", (event) => {
		if (event.button !== 0 || event.target.closest("button")) return;
		start = { y: event.clientY, time: performance.now() };
		element.setPointerCapture(event.pointerId);
		element.dataset.swiping = "true";
	});
	element.addEventListener("pointermove", (event) => {
		if (!start) return;
		const delta = event.clientY - start.y;
		// Up dismisses; down only gives a little, like a rubber band.
		element.style.setProperty("--swipe", `${delta < 0 ? delta : delta / (1.5 + delta / 20)}px`);
	});
	const end = (event) => {
		if (!start) return;
		const delta = event.clientY - start.y;
		const velocity = Math.abs(delta) / (performance.now() - start.time);
		start = null;
		delete element.dataset.swiping;
		if (delta < -SWIPE_THRESHOLD || (delta < 0 && velocity > 0.11)) {
			element.dataset.swipeOut = "true";
			dismiss(item.id);
		} else {
			element.style.setProperty("--swipe", "0px");
		}
	};
	element.addEventListener("pointerup", end);
	element.addEventListener("pointercancel", end);
}

function show(type, message, options = {}) {
	const id = options.id ?? `toast-${++nextId}`;
	let item = toasts.find((toast) => toast.id === id);
	mountToaster();
	if (item) {
		pauseTimer(item);
	} else {
		const element = document.createElement("li");
		element.className = "toast";
		element.tabIndex = 0;
		item = { id, element, height: 0, timer: null };
		toasts.unshift(item);
		toaster.append(element);
		bindSwipe(item);
		requestAnimationFrame(() => requestAnimationFrame(() => (element.dataset.mounted = "true")));
	}
	render(item, type, message, options);
	item.remaining = options.duration ?? DURATION[type] ?? DEFAULT_DURATION;
	layout();
	startTimer(item);
	return id;
}

function dismiss(id) {
	for (const item of id === undefined ? [...toasts] : toasts.filter((toast) => toast.id === id)) {
		pauseTimer(item);
		item.element.dataset.removed = "true";
		toasts.splice(toasts.indexOf(item), 1);
		setTimeout(() => {
			item.element.remove();
			if (!toasts.length && toaster?.matches(":popover-open")) {
				expanded = false;
				toaster.hidePopover();
			} else {
				setExpanded(engaged());
			}
		}, UNMOUNT_DELAY);
	}
	layout();
}

async function promise(work, { loading, success, error }) {
	const id = show("loading", loading);
	try {
		const value = await (typeof work === "function" ? work() : work);
		show("success", typeof success === "function" ? success(value) : success, { id });
		return value;
	} catch (failure) {
		show("error", typeof error === "function" ? error(failure) : error, { id });
		return undefined;
	}
}

export const toast = Object.assign((message, options) => show("default", message, options), {
	success: (message, options) => show("success", message, options),
	error: (message, options) => show("error", message, options),
	info: (message, options) => show("info", message, options),
	warning: (message, options) => show("warning", message, options),
	loading: (message, options) => show("loading", message, options),
	promise,
	dismiss,
});

/** Export feedback without blocking the page: the trigger is disabled until the file is saved or fails. */
export async function toastExport(trigger, work) {
	trigger.disabled = true;
	try {
		await toast.promise(work, {
			loading: gettext("Esportazione in corso…"),
			success: (filename) =>
				filename
					? interpolate(gettext("Scaricato «%(name)s»"), { name: filename }, true)
					: gettext("Esportazione completata"),
			error: gettext("Esportazione non riuscita. Riprova."),
		});
	} finally {
		trigger.disabled = false;
	}
}

/** Show a toast on the next page load (for actions that end in a reload). */
export function toastAfterReload(type, message, options = {}) {
	sessionStorage.setItem(PENDING_KEY, JSON.stringify({ type, message, options }));
}

document.addEventListener("visibilitychange", syncTimers);
document.addEventListener("keydown", (event) => {
	if (!event.altKey || event.code !== "KeyT" || !toasts.length) return;
	event.preventDefault();
	toasts[0].element.focus();
});

const pending = sessionStorage.getItem(PENDING_KEY);
if (pending) {
	sessionStorage.removeItem(PENDING_KEY);
	const { type, message, options } = JSON.parse(pending);
	const showPending = () => show(type, message, options);
	if (document.readyState === "loading")
		document.addEventListener("DOMContentLoaded", showPending, { once: true });
	else showPending();
}
